import { WidgetSDK, type IWidgetSDK } from '../core'
import type { WidgetInitFunction } from './widget.types'

const sdkInstances = new Map<ShadowRoot, IWidgetSDK>()
let currentInitializingSDK: IWidgetSDK | null = null

/**
 * `<gs-cc-registry-widget>` — Bridge for integrating external/third-party
 * widgets inside the community platform.
 *
 * Creates an SDK instance, executes widget scripts, and provides the
 * `init(sdk)` contract for registry widget authors. Content comes from the
 * widget registry via PHP `wrapInWebComponent()`.
 */
export class RegistryWidgetElement extends HTMLElement {
  private sdk: WidgetSDK | null = null

  static get observedAttributes(): string[] {
    return ['data-config']
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (name === 'data-config' && this.sdk && oldValue !== newValue) {
      try {
        const newProps = newValue ? JSON.parse(newValue) : {}
        this.sdk.setProps(newProps)
      } catch (error) {
        console.error('[WidgetSDK] Failed to parse data-config:', error)
      }
    }
  }

  connectedCallback(): void {
    if (this.shadowRoot) {
      this.initializeSDK(this.shadowRoot)
      return
    }

    const template = this.querySelector<HTMLTemplateElement>('template[shadowrootmode]')

    if (!template) {
      console.warn('[WidgetSDK] RegistryWidget: No shadow root or template found.')
      return
    }

    const mode = (template.getAttribute('shadowrootmode') ?? 'open') as ShadowRootMode
    const shadowRoot = this.attachShadow({ mode })
    shadowRoot.appendChild(template.content.cloneNode(true))
    template.remove()

    this.initializeSDK(shadowRoot)
  }

  disconnectedCallback(): void {
    if (this.sdk) {
      this.sdk._destroy()
      if (this.shadowRoot) {
        sdkInstances.delete(this.shadowRoot)
      }
      this.sdk = null
    }
  }

  private initializeSDK(shadowRoot: ShadowRoot): void {
    const widgetType = this.dataset.widgetType ?? 'unknown'
    const configAttr = this.dataset.config

    let props: Record<string, unknown> = {}
    if (configAttr) {
      try {
        props = JSON.parse(configAttr)
      } catch (error) {
        console.error('[WidgetSDK] Failed to parse data-config:', error)
      }
    }

    RegistryWidgetElement.injectDesignTokens(shadowRoot)

    this.sdk = new WidgetSDK({ widgetType, props, shadowRoot })
    sdkInstances.set(shadowRoot, this.sdk)
    this.executeScripts(shadowRoot)
    this.sdk._markReady()
  }

  private static injectDesignTokens(shadowRoot: ShadowRoot): void {
    if (shadowRoot.getElementById('css-variables')) return

    const source = document.getElementById('css-variables')
    if (!source || !(source instanceof HTMLStyleElement)) return

    const cssText = source.textContent
    if (!cssText) return

    const style = document.createElement('style')
    style.id = 'css-variables'
    style.textContent = cssText.replace(/html\s*\{/, ':host {')
    shadowRoot.prepend(style)
  }

  private executeScripts(root: ShadowRoot): void {
    const scripts = root.querySelectorAll('script')

    scripts.forEach((origScript) => {
      currentInitializingSDK = this.sdk

      try {
        const newScript = document.createElement('script')

        for (const attr of origScript.attributes) {
          newScript.setAttribute(attr.name, attr.value)
        }

        if (origScript.src) {
          newScript.src = origScript.src
          if (origScript.type === 'module') {
            this.handleModuleScript(origScript.src)
          }
        } else if (origScript.type === 'module' && origScript.textContent) {
          // Inline module script - convert to blob URL for dynamic import
          const blob = new Blob([origScript.textContent], { type: 'text/javascript' })
          const blobUrl = URL.createObjectURL(blob)
          this.handleModuleScript(blobUrl).finally(() => URL.revokeObjectURL(blobUrl))
        } else {
          newScript.textContent = origScript.textContent
        }

        origScript.parentNode?.replaceChild(newScript, origScript)
      } finally {
        currentInitializingSDK = null
      }
    })
  }

  private async handleModuleScript(src: string): Promise<void> {
    if (!this.sdk) return

    try {
      const module = await import(/* @vite-ignore */ src)

      if (typeof module.init === 'function') {
        await (module.init as WidgetInitFunction)(this.sdk)
      } else if (typeof module.default === 'function') {
        await (module.default as WidgetInitFunction)(this.sdk)
      }
    } catch (error) {
      console.error('[WidgetSDK] Failed to load/initialize module:', src, error)
      this.sdk.emit('error', {
        message: `Failed to load widget module: ${src}`,
        error: error instanceof Error ? error : new Error(String(error))
      })
    }
  }
}

export function getCurrentWidgetSDK(): IWidgetSDK | null {
  if (currentInitializingSDK) {
    return currentInitializingSDK
  }

  const currentScript = document.currentScript
  if (currentScript) {
    const root = currentScript.getRootNode()
    if (root instanceof ShadowRoot) {
      return sdkInstances.get(root) ?? null
    }
  }

  return null
}

export function getSDKForShadowRoot(shadowRoot: ShadowRoot): IWidgetSDK | null {
  return sdkInstances.get(shadowRoot) ?? null
}

if (typeof customElements !== 'undefined' && !customElements.get('gs-cc-registry-widget')) {
  customElements.define('gs-cc-registry-widget', RegistryWidgetElement)
}

if (globalThis.window !== undefined) {
  globalThis.window.getCurrentWidgetSDK = getCurrentWidgetSDK
  globalThis.window.__WIDGET_SDK_INSTANCES__ = sdkInstances
}
